import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Paper,
  Typography,
  Divider,
  Button,
  Container,
  CircularProgress,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const ProveedorDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [proveedor, setProveedor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const fetchProveedor = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/proveedores/${id}`);
        setProveedor(response.data);
        setError('');
      } catch (err) {
        setError('No se pudo cargar el proveedor.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchProveedor();
  }, [id]);

  const handleDeleteClick = () => {
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setDeleting(true);
      await axios.delete(`/api/proveedores/${id}`);
      setOpenDeleteDialog(false);
      navigate('/proveedores');
    } catch (err) {
      console.error('Error deleting proveedor:', err);
      setError('No se pudo eliminar el proveedor.');
      setOpenDeleteDialog(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
  };

  if (loading) {
    return <CircularProgress />;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  if (!proveedor) {
    return null;
  }

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 3, mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" gutterBottom>
            Detalle del Proveedor: {proveedor.nombre_comercial || proveedor.nombre_fiscal}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/proveedores/editar/${id}`)}
            >
              Editar
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteClick}
            >
              Eliminar
            </Button>
          </Box>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="body1"><strong>ID:</strong> {proveedor.id}</Typography>
        <Typography variant="body1"><strong>Nombre Fiscal:</strong> {proveedor.nombre_fiscal}</Typography>
        <Typography variant="body1"><strong>RFC:</strong> {proveedor.rfc}</Typography>
        <Typography variant="body1"><strong>Teléfono:</strong> {proveedor.telefono || 'N/A'}</Typography>
        <Typography variant="body1"><strong>Email:</strong> {proveedor.correo_electronico || 'N/A'}</Typography>
        <Typography variant="body1"><strong>Días de Crédito:</strong> {proveedor.dias_credito || 'N/A'}</Typography>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" sx={{ mt: 2 }}>Datos Bancarios</Typography>
        <Typography variant="body1"><strong>Banco:</strong> {proveedor.banco || 'N/A'}</Typography>
        <Typography variant="body1"><strong>Beneficiario:</strong> {proveedor.beneficiario || 'N/A'}</Typography>
        <Typography variant="body1"><strong>Número de Cuenta:</strong> {proveedor.numero_cuenta || 'N/A'}</Typography>
        <Typography variant="body1"><strong>CLABE:</strong> {proveedor.clabe || 'N/A'}</Typography>
        <Divider sx={{ my: 2 }} />
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/proveedores')}
          sx={{ mt: 2 }}
        >
          Volver a la Lista
        </Button>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Confirmar Eliminación
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            ¿Está seguro de que desea eliminar al proveedor "{proveedor?.nombre_fiscal}"?
            Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            autoFocus
          >
            {deleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProveedorDetail;
